import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { logoutAdmin, checkAdminAuth, handleAdminAuthError } from '../utils/auth';
import './AdminDeposits.css';

import { API_URL } from '../config/data';

const AdminDeposits = () => {
  const navigate = useNavigate();
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [editingOperation, setEditingOperation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    amount: '',
    currency: '',
    status: '',
    account_number: '',
    contact_method: '',
    comment: '',
    created_at_local: ''
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [userAccounts, setUserAccounts] = useState([]);
  const [createFormData, setCreateFormData] = useState({
    userId: '',
    operationType: 'deposit',
    accountId: '',
    amount: '',
    currency: 'RUB',
    comment: '',
    status: 'created',
    contactMethod: '',
    createdAtLocal: ''
  });
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingAccounts, setLoadingAccounts] = useState(false);

  useEffect(() => {
    const initAdmin = async () => {
      try {
        await checkAdminAuth();
        await fetchOperations();
      } catch (error) {
        handleAdminAuthError(error);
      }
    };

    initAdmin();
  }, [navigate]);

  const fetchOperations = async (page = 1) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      console.log('AdminDeposits - Token:', token ? 'Present' : 'Missing');
      console.log('AdminDeposits - Token value:', token);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        type: 'deposit'
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }
      if (filterStatus !== 'all') {
        params.append('status', filterStatus);
      }
      if (filterCurrency !== 'all') {
        params.append('currency', filterCurrency);
      }

      const response = await axios.get(`${API_URL}/api/admin/operations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setOperations(response.data.operations || []);
      setTotalPages(response.data.pagination?.pages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching deposits:', error);
      setError('Ошибка загрузки операций пополнения');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchOperations(1);
  };

  const handleStatusChange = async (operationId, newStatus) => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/admin/operations/${operationId}`, {
        status: newStatus
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchOperations(currentPage);
    } catch (error) {
      console.error('Error updating operation status:', error);
      setError('Ошибка изменения статуса операции');
    }
  };

  const handleDeleteOperation = async (operationId) => {
    const operation = operations.find(op => op.id === operationId);
    const isCompleted = operation?.status === 'completed';
    
    const confirmMessage = isCompleted 
      ? `ВНИМАНИЕ! Вы собираетесь удалить ЗАВЕРШЕННУЮ операцию. Вы уверены, что хотите продолжить?`
      : 'Вы уверены, что хотите удалить эту операцию? Это действие нельзя отменить.';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      await axios.delete(`${API_URL}/api/admin/operations/${operationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      await fetchOperations(currentPage);
    } catch (error) {
      console.error('Error deleting operation:', error);
      setError('Ошибка удаления операции: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleEditOperation = (operation) => {
    setEditingOperation(operation);
    setEditFormData({
      amount: operation.amount || '',
      currency: operation.currency || '',
      status: operation.status || '',
      account_number: operation.account_number || '',
      contact_method: operation.contact_method || '',
      comment: operation.comment || '',
      created_at_local: operation.created_at ? new Date(operation.created_at).toISOString().slice(0,16) : ''
    });
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const token = localStorage.getItem('token');
      
      await axios.put(`${API_URL}/api/admin/operations/${editingOperation.id}`, {
        amount: parseFloat(editFormData.amount),
        currency: editFormData.currency,
        status: editFormData.status,
        account_number: editFormData.account_number,
        contact_method: editFormData.contact_method,
        comment: editFormData.comment,
        created_at: editFormData.created_at_local ? new Date(editFormData.created_at_local).toISOString() : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setEditingOperation(null);
      setEditFormData({
        amount: '',
        currency: '',
        status: '',
        account_number: '',
        contact_method: '',
        comment: '',
        created_at_local: ''
      });
      await fetchOperations(currentPage);
    } catch (error) {
      console.error('Error updating operation:', error);
      setError('Ошибка обновления операции');
    }
  };

  const handleCancelEdit = () => {
    setEditingOperation(null);
    setEditFormData({
      amount: '',
      currency: '',
      status: '',
      account_number: '',
      contact_method: '',
      comment: '',
      created_at_local: ''
    });
  };

  const formatCurrency = (amount, currency) => {
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: currency || 'RUB'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      'created': 'Создана',
      'processing': 'Обрабатывается',
      'completed': 'Завершена',
      'rejected': 'Отклонена'
    };
    return statusMap[status] || status;
  };

  const getStatusClass = (status) => {
    return `admin-status admin-status--${status}`;
  };

  const handleLogout = () => {
    logoutAdmin();
  };

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Ошибка загрузки пользователей');
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchUserAccounts = async (userId) => {
    if (!userId) {
      setUserAccounts([]);
      return;
    }
    
    try {
      setLoadingAccounts(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/users/${userId}/accounts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching user accounts:', error);
      setError('Ошибка загрузки счетов пользователя');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleCreateModalOpen = () => {
    setShowCreateModal(true);
    fetchUsers();
  };

  const handleCreateModalClose = () => {
    setShowCreateModal(false);
    setCreateFormData({
      userId: '',
      operationType: 'deposit',
      accountId: '',
      amount: '',
      currency: 'RUB',
      comment: '',
      status: 'created',
      contactMethod: '',
      createdAtLocal: ''
    });
    setUserAccounts([]);
  };

  const handleCreateFormChange = (e) => {
    const { name, value } = e.target;
    setCreateFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'userId') {
      fetchUserAccounts(value);
      setCreateFormData(prev => ({
        ...prev,
        accountId: ''
      }));
    }
  };

  const handleCreateOperation = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const operationData = {
        userId: parseInt(createFormData.userId),
        operationType: createFormData.operationType,
        accountId: parseInt(createFormData.accountId),
        amount: parseFloat(createFormData.amount),
        currency: createFormData.currency,
        comment: createFormData.comment,
        status: createFormData.status,
        contactMethod: createFormData.contactMethod,
        created_at: createFormData.createdAtLocal ? new Date(createFormData.createdAtLocal).toISOString() : undefined
      };

      await axios.post(`${API_URL}/api/admin/operations/create`, operationData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      handleCreateModalClose();
      await fetchOperations(currentPage);
    } catch (error) {
      console.error('Error creating operation:', error);
      setError('Ошибка создания операции: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loading__spinner"></div>
        <p>Загрузка операций пополнения...</p>
      </div>
    );
  }

  return (
    <div className="admin-deposits">
      <div className="admin-header">
        <div className="admin-header__logo">
          <img src="/assets/img/logo.svg" alt="Pro Investor" />
        </div>
        <div className="admin-header__title">Пополнения</div>
        <div className="admin-header__actions">
          <button 
            className="admin-header__back" 
            onClick={() => navigate('/admin')}
          >
            <span className="--icon-arrow-left"></span>
            Назад
          </button>
          <button className="admin-header__logout" onClick={handleLogout}>
            <span className="--icon-log-out"></span>
            Выйти
          </button>
        </div>
      </div>

      <div className="admin-content">
        {error && (
          <div className="admin-error">
            <p>{error}</p>
            <button className="admin-btn admin-btn--primary" onClick={() => setError('')}>
              Закрыть
            </button>
          </div>
        )}

        <div className="admin-deposits__filters">
          <div className="admin-filters__top">
            <form onSubmit={handleSearch} className="admin-search-form">
              <input
                type="text"
                placeholder="Поиск по пользователю или номеру счета..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-search-input"
              />
              <button type="submit" className="admin-search-btn">
                <span className="--icon-search"></span>
              </button>
            </form>

            <button 
              className="admin-btn admin-btn--primary admin-btn--create"
              onClick={handleCreateModalOpen}
            >
              <span className="--icon-plus"></span>
              Создать операцию
            </button>
          </div>

          <div className="admin-filters">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">Все статусы</option>
              <option value="created">Создана</option>
              <option value="processing">Обрабатывается</option>
              <option value="completed">Завершена</option>
              <option value="rejected">Отклонена</option>
            </select>

            <select
              value={filterCurrency}
              onChange={(e) => setFilterCurrency(e.target.value)}
              className="admin-filter-select"
            >
              <option value="all">Все валюты</option>
              <option value="RUB">RUB</option>
              <option value="USD">USD</option>
              <option value="EUR">EUR</option>
            </select>
          </div>
        </div>

        <div className="admin-deposits__table-container">
          <table className="admin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Пользователь</th>
                <th>Счет</th>
                <th>Сумма</th>
                <th>Валюта</th>
                <th>Статус</th>
                <th>Дата создания</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {operations.map((operation) => (
                <tr key={operation.id}>
                  <td className="admin-table__id">{operation.id}</td>
                  <td className="admin-table__user">
                    <div className="admin-table__user-info">
                      <div className="admin-table__user-name">
                        {operation.first_name} {operation.last_name}
                      </div>
                      <div className="admin-table__user-email">
                        {operation.email}
                      </div>
                    </div>
                  </td>
                  <td className="admin-table__account">
                    {operation.account_number || 'Не указан'}
                  </td>
                  <td className="admin-table__amount">
                    {formatCurrency(operation.amount, operation.currency)}
                  </td>
                  <td className="admin-table__currency">
                    {operation.currency}
                  </td>
                  <td className="admin-table__status">
                    <span className={getStatusClass(operation.status)}>
                      {getStatusText(operation.status)}
                    </span>
                  </td>
                  <td className="admin-table__date">
                    {operation.contact_method ? operation.contact_method : formatDate(operation.created_at)}
                  </td>
                  <td className="admin-table__actions">
                    <div className="admin-table__action-buttons">
                      <button
                        className="admin-btn admin-btn--primary"
                        onClick={() => handleEditOperation(operation)}
                        style={{ marginBottom: '0.5rem' }}
                      >
                        Редактировать
                      </button>
                      {operation.status === 'created' && (
                        <>
                          <button
                            className="admin-btn admin-btn--success"
                            onClick={() => handleStatusChange(operation.id, 'processing')}
                          >
                            Обработать
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            onClick={() => handleStatusChange(operation.id, 'rejected')}
                          >
                            Отклонить
                          </button>
                        </>
                      )}
                      {operation.status === 'processing' && (
                        <>
                          <button
                            className="admin-btn admin-btn--success"
                            onClick={() => handleStatusChange(operation.id, 'completed')}
                          >
                            Завершить
                          </button>
                          <button
                            className="admin-btn admin-btn--danger"
                            onClick={() => handleStatusChange(operation.id, 'rejected')}
                          >
                            Отклонить
                          </button>
                        </>
                      )}
                      {operation.status === 'completed' && (
                        <span className="admin-table__completed-text">
                          Завершена
                        </span>
                      )}
                      {operation.status === 'rejected' && (
                        <span className="admin-table__rejected-text">
                          Отклонена
                        </span>
                      )}
                      {/* Кнопка удаления - показываем для всех операций */}
                      <button
                        className="admin-btn admin-btn--danger"
                        onClick={() => handleDeleteOperation(operation.id)}
                        style={{ marginTop: '0.5rem', backgroundColor: '#dc3545', borderColor: '#dc3545' }}
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {operations.length === 0 && (
            <div className="admin-empty">
              <h2>Операции пополнения не найдены</h2>
              <p>Попробуйте изменить параметры поиска или фильтры</p>
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="admin-pagination">
            <button
              className="admin-pagination__btn"
              onClick={() => fetchOperations(currentPage - 1)}
              disabled={currentPage === 1}
            >
              Назад
            </button>
            
            <div className="admin-pagination__info">
              Страница {currentPage} из {totalPages}
            </div>
            
            <button
              className="admin-pagination__btn"
              onClick={() => fetchOperations(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              Вперед
            </button>
          </div>
        )}
      </div>

      {/* Модальное окно для редактирования операции */}
      {editingOperation && (
        <div className="admin-modal">
          <div className="admin-modal__overlay" onClick={handleCancelEdit}></div>
          <div className="admin-modal__content">
            <div className="admin-modal__header">
              <h2>Редактирование операции #{editingOperation.id}</h2>
              <button 
                className="admin-modal__close" 
                onClick={handleCancelEdit}
              >
                ×
              </button>
            </div>
            
            <div className="admin-modal__body">
              <form onSubmit={(e) => { e.preventDefault(); handleSaveEdit(); }} className="admin-edit-form">
                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="amount">Сумма *</label>
                    <input
                      type="number"
                      id="amount"
                      name="amount"
                      value={editFormData.amount}
                      onChange={handleEditFormChange}
                      className="admin-edit-form__input"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>

                  <div className="admin-edit-form__field">
                    <label htmlFor="currency">Валюта *</label>
                    <select
                      id="currency"
                      name="currency"
                      value={editFormData.currency}
                      onChange={handleEditFormChange}
                      className="admin-edit-form__input"
                      required
                    >
                      <option value="RUB">RUB</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="status">Статус *</label>
                    <select
                      id="status"
                      name="status"
                      value={editFormData.status}
                      onChange={handleEditFormChange}
                      className="admin-edit-form__input"
                      required
                    >
                      <option value="created">Создана</option>
                      <option value="processing">Обрабатывается</option>
                      <option value="completed">Завершена</option>
                      <option value="rejected">Отклонена</option>
                    </select>
                  </div>

                  <div className="admin-edit-form__field">
                    <label htmlFor="account_number">Номер счета</label>
                    <input
                      type="text"
                      id="account_number"
                      name="account_number"
                      value={editFormData.account_number}
                      onChange={handleEditFormChange}
                      className="admin-edit-form__input"
                      placeholder="Введите номер счета"
                    />
                  </div>
                  <div className="admin-edit-form__field">
                    <label htmlFor="created_at_local">Дата и время</label>
                    <input
                      type="datetime-local"
                      id="created_at_local"
                      name="created_at_local"
                      value={editFormData.created_at_local}
                      onChange={handleEditFormChange}
                      className="admin-edit-form__input"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__row">
                  <div className="admin-edit-form__field">
                    <label htmlFor="comment">Комментарий</label>
                    <input
                      type="text"
                      id="comment"
                      name="comment"
                      value={editFormData.comment}
                      onChange={handleEditFormChange}
                      className="admin-edit-form__input"
                      placeholder="Комментарий к операции"
                    />
                  </div>
                </div>

                <div className="admin-edit-form__actions">
                  <button 
                    type="button"
                    className="admin-btn admin-btn--secondary" 
                    onClick={handleCancelEdit}
                  >
                    Отмена
                  </button>
                  <button 
                    type="submit"
                    className="admin-btn admin-btn--primary" 
                  >
                    Сохранить
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно для создания операции */}
      {showCreateModal && (
        <div className="admin-modal" onClick={handleCreateModalClose}>
          <div className="admin-modal__overlay"></div>
          <div className="admin-modal__content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal__header">
              <h2>Создание операции пополнения</h2>
              <button 
                className="admin-modal__close" 
                onClick={handleCreateModalClose}
              >
                ×
              </button>
            </div>
            
            <div className="admin-modal__body">
              <div className="admin-create-form">
                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Пользователь *</label>
                    <select
                      name="userId"
                      value={createFormData.userId}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__select"
                      required
                      disabled={loadingUsers}
                    >
                      <option value="">Выберите пользователя</option>
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name} ({user.email})
                        </option>
                      ))}
                    </select>
                    {loadingUsers && <div className="admin-loading-text">Загрузка пользователей...</div>}
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Счет *</label>
                    <select
                      name="accountId"
                      value={createFormData.accountId}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__select"
                      required
                      disabled={!createFormData.userId || loadingAccounts}
                    >
                      <option value="">Выберите счет</option>
                      {userAccounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.account_number} ({account.currency}) - Торговый - {formatCurrency(Number(account.balance), account.currency)}
                        </option>
                      ))}
                    </select>
                    {loadingAccounts && <div className="admin-loading-text">Загрузка счетов...</div>}
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Сумма *</label>
                    <input
                      type="number"
                      name="amount"
                      value={createFormData.amount}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                      step="0.01"
                      min="0"
                      required
                    />
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Валюта *</label>
                    <select
                      name="currency"
                      value={createFormData.currency}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__select"
                      required
                    >
                      <option value="RUB">RUB</option>
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                    </select>
                  </div>
                </div>

                <div className="admin-create-form__row">
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Статус</label>
                    <select
                      name="status"
                      value={createFormData.status}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__select"
                    >
                      <option value="created">Создана</option>
                      <option value="processing">Обрабатывается</option>
                      <option value="completed">Завершена</option>
                      <option value="rejected">Отклонена</option>
                    </select>
                  </div>
                  
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Комментарий</label>
                    <input
                      type="text"
                      name="comment"
                      value={createFormData.comment}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                      placeholder="Комментарий к операции"
                    />
                  </div>
                  <div className="admin-create-form__field">
                    <label className="admin-create-form__label">Дата и время</label>
                    <input
                      type="datetime-local"
                      name="createdAtLocal"
                      value={createFormData.createdAtLocal}
                      onChange={handleCreateFormChange}
                      className="admin-create-form__input"
                    />
                  </div>
                </div>
              </div>
            </div>
            
            <div className="admin-modal__footer">
              <div className="admin-create-form__actions">
                <button
                  className="admin-btn admin-btn--secondary"
                  onClick={handleCreateModalClose}
                >
                  Отмена
                </button>
                <button
                  className="admin-btn admin-btn--primary"
                  onClick={handleCreateOperation}
                  disabled={!createFormData.userId || !createFormData.accountId || !createFormData.amount}
                >
                  Создать операцию
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDeposits;
